/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
    Cell,
    Container,
    Grid,
    GridContainer,
    GridWrapper,
    Overlay,
    RowWrapper,
    TitleBox,
    ZoomButton,
    ZoomContainer,
} from 'components/auditorium/auditorium.style'
import { CustomRowButton } from 'components/button/rowButton'
import { useDrag, useZoom } from 'hooks/user/useAuditoriumSeats'
import { Tooltip } from 'primereact/tooltip'
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { CreateTicket } from 'types/Event'
import { Seat } from 'types/Location'
import { generateSeatLabelData } from 'utils/seatLabel'

import MiddleColumnDisplay from '../basic/MiddleColumnDisplay'
import seatIcon from './seat.svg'

interface Props {
    rows: number | null
    columns: number | null
    seats: Seat[]
    ticketData: CreateTicket[]
    setTicketData: Dispatch<SetStateAction<CreateTicket[]>>
    selectedPrice: number | null
    eventId?: string
    middleColumn?: number
    isGallery?: boolean
    galleryName?: string
}

export const AuditoriumPricing: React.FC<Props> = ({
    rows,
    columns,
    seats,
    ticketData,
    setTicketData,
    selectedPrice,
    eventId,
    middleColumn,
    isGallery = false,
    galleryName = 'Karzat',
}) => {
    const [selectedPriceIndex, setSelectedPriceIndex] = useState<number | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const { containerRef, handleDragStart, handleDragMove, handleDragEnd } = useDrag()
    const { zoomLevel, handleZoomIn, handleZoomOut } = useZoom(columns || 0)

    useEffect(() => {
        setLoading(false)
    }, [rows, columns])

    useEffect(() => {
        if (containerRef.current && !loading) {
            const container = containerRef.current
            container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
        }
    }, [containerRef, loading, rows, columns])

    const isSeat = useCallback(
        (row: number, column: number): boolean => seats?.some((seatItem) => seatItem.row === row && seatItem.column === column),
        [seats],
    )

    const getPrice = useCallback(
        (row: number, column: number): number | undefined => {
            const seatId = seats.find((seat) => seat.row === row && seat.column === column)?.seat_id
            const ticket = ticketData.find((ticket) => ticket.seat_id === seatId)
            return ticket ? ticket.price : undefined
        },
        [ticketData, seats],
    )

    const updateTicketData = useCallback(
        (row: number, price?: number) => {
            const updatedTicketData = [...ticketData]
            seats
                .filter((seat) => seat.row === row)
                .forEach((seat) => {
                    const seatId = seat.seat_id!
                    const index = updatedTicketData.findIndex((pricing) => pricing.seat_id === seatId)
                    if (price === undefined) {
                        if (index !== -1) {
                            updatedTicketData.splice(index, 1)
                        }
                    } else if (index !== -1) {
                        updatedTicketData[index].price = price
                    } else {
                        updatedTicketData.push({
                            seat_id: seatId,
                            price,
                            ...(eventId ? { event_id: eventId } : {}),
                        })
                    }
                })
            setTicketData(updatedTicketData)
        },
        [ticketData, seats, setTicketData, eventId],
    )

    const handleSeatClick = useCallback(
        (row: number, column: number) => {
            if (selectedPrice === null || !isSeat(row, column)) return

            const seatId = seats.find((seat) => seat.row === row && seat.column === column)?.seat_id
            const currentPrice = getPrice(row, column)

            setTicketData((prev) => {
                const updatedTicketData = [...prev]
                const index = updatedTicketData.findIndex((pricing) => pricing.seat_id === seatId)

                if (currentPrice !== undefined) {
                    if (currentPrice === selectedPrice) {
                        if (index !== -1) {
                            updatedTicketData.splice(index, 1)
                        }
                    } else if (index !== -1) {
                        updatedTicketData[index].price = selectedPrice
                    } else {
                        updatedTicketData.push({ seat_id: seatId!, price: selectedPrice, ...(eventId ? { event_id: eventId } : {}) })
                    }
                } else {
                    updatedTicketData.push({ seat_id: seatId!, price: selectedPrice, ...(eventId ? { event_id: eventId } : {}) })
                }

                return updatedTicketData
            })
        },
        [getPrice, isSeat, selectedPrice, seats, setTicketData, eventId],
    )

    const getMostFrequentPrice = (rowIndex: number): number | null => {
        const prices = seats
            .filter((seat) => seat.row === rowIndex)
            .map((seat) => {
                const ticket = ticketData.find((t) => t.seat_id === seat.seat_id)
                return ticket?.price
            })
            .filter((p): p is number => p !== undefined)

        if (prices.length === 0) return null

        const frequencyMap = prices.reduce<Record<number, number>>((acc, price) => {
            acc[price] = (acc[price] || 0) + 1
            return acc
        }, {})

        let maxCount = 0
        let modePrice: number | null = null
        for (const [priceStr, count] of Object.entries(frequencyMap)) {
            const price = Number(priceStr)
            if (count > maxCount) {
                maxCount = count
                modePrice = price
            }
        }

        return modePrice
    }

    const handleRowButtonClick = useCallback(
        (row: number) => {
            if (selectedPrice === null) return

            const seatsInRow = seats.filter((seat) => seat.row === row)
            const hasAssignedPrice = seatsInRow.some((seat) => ticketData.some((ticket) => ticket.seat_id === seat.seat_id))

            if (hasAssignedPrice) {
                updateTicketData(row, undefined)
                setSelectedPriceIndex(null)
            } else {
                updateTicketData(row, selectedPrice)
                setSelectedPriceIndex(row)
            }
        },
        [selectedPrice, seats, ticketData, updateTicketData],
    )

    if (!rows || !columns) return null

    return (
        <Container>
            <TitleBox>Színpad</TitleBox>
            <GridContainer
                ref={containerRef}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
            >
                <GridWrapper style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center', transition: 'transform 0.3s ease' }}>
                    <MiddleColumnDisplay columns={columns} selectedMiddleColumn={middleColumn} />

                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <RowWrapper key={rowIndex}>
                            <CustomRowButton
                                price={getMostFrequentPrice(rowIndex) ?? selectedPrice ?? 0}
                                hasAssignedPrice={seats
                                    .filter((seat) => seat.row === rowIndex)
                                    .some((seat) => ticketData.some((ticket) => ticket.seat_id === seat.seat_id))}
                                onClick={() => handleRowButtonClick(rowIndex)}
                                selectedPrice={selectedPrice}
                            />
                            <Grid rows={1} columns={columns}>
                                {Array.from({ length: columns }).map((_, columnIndex) => {
                                    const price = getPrice(rowIndex, columnIndex)
                                    const hasSeat = isSeat(rowIndex, columnIndex)

                                    return (
                                        <div key={`seat-fragment-${rowIndex}-${columnIndex}`}>
                                            <Cell
                                                id={`seat-${rowIndex}-${columnIndex}`}
                                                onClick={() => handleSeatClick(rowIndex, columnIndex)}
                                                style={{ cursor: hasSeat ? 'pointer' : 'default' }}
                                                highlight={middleColumn === columnIndex}
                                            >
                                                {hasSeat && <img src={seatIcon} alt="Seat" />}
                                                {price !== undefined && (
                                                    <Overlay>
                                                        <span>{price} Ft</span>
                                                    </Overlay>
                                                )}
                                            </Cell>
                                            <Tooltip
                                                target={`#seat-${rowIndex}-${columnIndex}`}
                                                content={
                                                    generateSeatLabelData(
                                                        rowIndex,
                                                        columnIndex,
                                                        columns,
                                                        seats,
                                                        middleColumn,
                                                        isGallery,
                                                        galleryName,
                                                    )?.label
                                                }
                                                position="right"
                                                showOnDisabled
                                                showDelay={400}
                                                hideDelay={200}
                                            />
                                        </div>
                                    )
                                })}
                            </Grid>
                        </RowWrapper>
                    ))}
                </GridWrapper>
            </GridContainer>
            <ZoomContainer>
                <ZoomButton type="button" onClick={handleZoomIn} icon="pi pi-search-plus" />
                <ZoomButton type="button" onClick={handleZoomOut} icon="pi pi-search-minus" />
            </ZoomContainer>
            <TitleBox>{`${seats.length} db férőhely`}</TitleBox>
        </Container>
    )
}
